import type { IntlShape } from 'react-intl'
import { defineMessages } from 'react-intl'
import { formatDate } from '@luxuryescapes/lib-refunds'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'

enum Refundable {
  Fully = 'fully_refundable',
  Partial = 'partially_refundable',
  Non = 'non_refundable',
}

const REFUND_DESCRIPTION_MESSAGES = defineMessages({
  freeCancellation: {
    id: 'RiBzt-x0PY3xMPKTNlQx',
    defaultMessage: 'Free cancellation',
    description: 'Refund status label for fully refundable offers in the cancellation flow',
  },
  flexibleCancellation: {
    id: 'ln4DPEiktENvZxyPAkso',
    defaultMessage: 'Flexible cancellation',
    description: 'Refund status label for flexible cancellation (rental offers)',
  },
  partiallyRefundable: {
    id: '3D3t065AJCyfvixbLm_J',
    defaultMessage: 'Partially refundable',
    description: 'Refund status label for partially refundable offers',
  },
  nonRefundable: {
    id: 'zQuPvFb46w86y5kIxKNG',
    defaultMessage: 'Non refundable',
    description: 'Refund status label for non-refundable offers in the cancellation flow',
  },
})

const getRefundDescriptions = (offerType?: string, intl?: IntlShape): { [key in Refundable]: string } => {
  const formatMessage = (message: (typeof REFUND_DESCRIPTION_MESSAGES)[keyof typeof REFUND_DESCRIPTION_MESSAGES]) => {
    if (intl) {
      return intl.formatMessage(message)
    }

    return message.defaultMessage ?? ''
  }

  return {
    [Refundable.Fully]: formatMessage(
      offerType === 'rental'
        ? REFUND_DESCRIPTION_MESSAGES.flexibleCancellation
        : REFUND_DESCRIPTION_MESSAGES.freeCancellation,
    ),
    [Refundable.Partial]: formatMessage(REFUND_DESCRIPTION_MESSAGES.partiallyRefundable),
    [Refundable.Non]: formatMessage(REFUND_DESCRIPTION_MESSAGES.nonRefundable),
  }
}
interface SimplifiedPolicy {
  policy: {
    unit: 'percentage' | 'amount'
    leadDaysMin: number
    value: number
  }
  nonRefundable: boolean | undefined
}
// If nonRefundable does not exist then its implied that any cancellations greater than the max leadDaysMin are refundable
// using the leadDaysMax as the cutoff
const generateRefundablePolicy = (cancellationPolicies: Array<App.RoomRateCancellationPolicy>): SimplifiedPolicy | null => {
  if (!cancellationPolicies || cancellationPolicies.length === 0) return null

  const policyWithMaxLeadDaysMin = cancellationPolicies.reduce((maxPolicy, currentPolicy) =>
    currentPolicy.policy.leadDaysMin > maxPolicy.policy.leadDaysMin ? currentPolicy : maxPolicy, cancellationPolicies[0])
  if (!policyWithMaxLeadDaysMin || policyWithMaxLeadDaysMin?.nonRefundable) return null
  return {
    policy: {
      unit: 'percentage',
      leadDaysMin: policyWithMaxLeadDaysMin.policy.leadDaysMax ?? 365, // we use leadDaysMax as this new refundable policy needs to start at the end of the last policy
      value: 0,
    },
    nonRefundable: false,
  }
}

const getRefundStatus = (cancellationPolicies: Array<App.RoomRateCancellationPolicy>) => {
  // Non policy displayed
  if (!cancellationPolicies || cancellationPolicies.length === 0) return null
  // If all policies are non refundable, display non refundable
  if (cancellationPolicies.every(policy => policy.nonRefundable)) return Refundable.Non

  if (generateRefundablePolicy(cancellationPolicies)) {
    return Refundable.Fully
  }

  if (cancellationPolicies.some(policy =>
    (policy.policy.unit === 'percentage' && policy.policy.value === 0) ||
    (policy.policy.unit === 'amount' && policy.policy.value === 0))) {
    return Refundable.Fully
  } else if (cancellationPolicies.some(policy =>
    (policy.policy.unit === 'percentage' && policy.policy.value > 0 && policy.policy.value < 100) ||
    (policy.policy.unit === 'amount' && policy.policy.value > 0))) {
    return Refundable.Partial
  } else {
    return Refundable.Non
  }
}

function getPropertyRefundableStatus(headlineCancellationPolicyType: 'fullyRefundable' | 'partiallyRefundable' | 'nonRefundable'): Refundable {
  switch (headlineCancellationPolicyType) {
    case 'fullyRefundable':
      return Refundable.Fully
    case 'partiallyRefundable':
      return Refundable.Partial
    case 'nonRefundable':
      return Refundable.Non
    default:
      assertUnreachable(headlineCancellationPolicyType)
  }
}

type PropertyCancellationLabelShape =
  | { kind: 'non-refundable' }
  | { kind: 'no-date' }
  | { kind: 'date', formattedDate: string, hotelOnly: boolean }

/**
 * Single source of truth for the Property offer-page cancellation label's date/hotel-only
 * decision tree -- previously defined locally in `PropertyCancellationPolicy.tsx` (the real
 * rendered component), moved here so the offer-page assistant snapshot builder can call the exact
 * same derivation instead of maintaining a separate mirror. Callers pick their own presentation
 * (JSX with `<FormattedMessage>`, or a plain string) based on the returned shape.
 */
function getPropertyCancellationLabelShape({
  refundable,
  headlineCancellationPolicyStart,
  propertyTimezone,
  shouldClarifyRefundIsHotelOnly,
}: {
  refundable: Refundable
  headlineCancellationPolicyStart: Date | undefined
  propertyTimezone: string
  shouldClarifyRefundIsHotelOnly: boolean
}): PropertyCancellationLabelShape {
  if (refundable === Refundable.Non) {
    return { kind: 'non-refundable' }
  }
  if (!headlineCancellationPolicyStart) {
    return { kind: 'no-date' }
  }
  const formattedDate = formatDate.formattedShortDateByTimezone(headlineCancellationPolicyStart, propertyTimezone)
  return { kind: 'date', formattedDate, hotelOnly: shouldClarifyRefundIsHotelOnly }
}

export {
  getRefundStatus,
  Refundable,
  getRefundDescriptions,
  generateRefundablePolicy,
  getPropertyRefundableStatus,
  getPropertyCancellationLabelShape,
}
export type { PropertyCancellationLabelShape }
